import {
	readCallBookingConfig,
	findCallBookingFields,
	earliestBookableDate,
	latestBookableDate,
	isOpenOn,
	toIsoDate,
	slotToMinutes,
	describeSlot,
	slotOptionLabel,
	guestTimeDescription,
	slotNearestLocalTime,
	guestTimeZone,
	lookupGuestGeo,
	guestPlace,
	timeZoneAbbreviation,
	guestZoneAbbreviation,
	prettyTimeZoneName,
} from '../../assets/scripts/utils/call-booking.js';

// Configure call-date datepicker options via GF's own filter so they are
// baked in at init time.  This avoids calling .datepicker('option') post-init
// which can cause the shared #ui-datepicker-div to render unstyled.
if (typeof gform !== 'undefined' && typeof gform.addFilter === 'function') {
	gform.addFilter('gform_datepicker_options_pre_init', function (optionsObj, formId, fieldId, $element) {
		var isGetInTouch = $element.closest('.Get__In__Touch__Form').length > 0;
		var prevBeforeShow = optionsObj.beforeShow;

		optionsObj.beforeShow = function (input, inst) {
			inst.dpDiv[0].classList.remove('GetInTouchForm-datepicker');
			var ret = prevBeforeShow ? prevBeforeShow.apply(this, arguments) : true;
			if (ret !== false && isGetInTouch) {
				inst.dpDiv[0].classList.add('GetInTouchForm-datepicker');
			}
			return ret;
		};

		if (!isGetInTouch) {
			return optionsObj;
		}

		// The bookable window, from the same config the server validates
		// against. The old version derived "next working day" from the browser's
		// own day, which is a different day to ours for a guest in the Americas
		// late in their evening — and left every past day of the current month
		// selectable in the dropdown variant.
		var config = readCallBookingConfig(null);
		var earliest = earliestBookableDate(config).split('-').map(Number);

		optionsObj.minDate = new Date(earliest[0], earliest[1] - 1, earliest[2]);
		optionsObj.beforeShowDay = function (date) {
			return [isOpenOn(config, date.getFullYear(), date.getMonth() + 1, date.getDate()), ''];
		};

		return optionsObj;
	});
}

if (typeof window !== 'undefined' && window.intlTelInput && document.querySelector('.Get__In__Touch__Form')) {
	window.intlTelInput.defaults.useFullscreenPopup = false;
}

class GetInTouchForm {
	showChildrenGuestsSelector = false;

	isLoading = false;

	imageLoaded = true;

	defaultImageUrl = '/wp-content/themes/pegasus/assets/img/form-images/yellow-canyon.jpg';

	currentImage = '/wp-content/themes/pegasus/assets/img/form-images/yellow-canyon.jpg';

	currentReviewText = "ATR are experts in their field and I wouldn't trust anyone else.";

	currentReviewerName = 'Tara Stewart';

	defaultReview = {
		text: "ATR are experts in their field and I wouldn't trust anyone else.",
		name: 'Tara Stewart',
	};

	locationDefaultReviewMap = {};

	locationStepReviewMap = {};

	boundValidationHandlers = new Set();

	setPhoneFieldInvalidState(field, shouldBeInvalid) {
		if (!field) {
			return;
		}

		const phoneField = field.closest('.gfield');
		const phoneContainer = field.closest('.ginput_container_phone');
		const itiContainer = phoneContainer ? phoneContainer.querySelector('.iti') : null;

		field.classList.toggle('is-invalid-required', shouldBeInvalid);
		if (phoneField) {
			phoneField.classList.toggle('gfield--phone-required-error', shouldBeInvalid);
		}
		if (phoneContainer) {
			phoneContainer.classList.toggle('is-invalid-required', shouldBeInvalid);
		}
		if (itiContainer) {
			itiContainer.classList.toggle('is-invalid-required', shouldBeInvalid);
		}
	}

	isPhoneFieldInvalid(field) {
		if (!field) {
			return false;
		}

		const isRequired = this.isPhoneFieldRequired(field);
		const valueIsEmpty = !field.value || field.value.trim() === '';
		const phoneContainer = field.closest('.ginput_container_phone');
		const pluginError = phoneContainer ? phoneContainer.querySelector('.gfipm-error-msg') : null;

		// Empty is only invalid when the phone field is required.
		// Plugin errors should only invalidate once a value exists.
		return (isRequired && valueIsEmpty) || (!valueIsEmpty && Boolean(pluginError));
	}

	isPhoneFieldRequired(field) {
		if (!field) {
			return false;
		}

		const gfield = field.closest('.gfield');
		return field.required || field.getAttribute('aria-required') === 'true' || (gfield && gfield.classList.contains('gfield_contains_required'));
	}

	setNameFieldInvalidState(field, shouldBeInvalid) {
		if (!field) {
			return;
		}

		const nameField = field.closest('.gfield');
		field.classList.toggle('is-invalid-required', shouldBeInvalid);
		if (nameField) {
			nameField.classList.toggle('gfield--name-required-error', shouldBeInvalid);
		}
	}

	isNameFieldInvalid(field) {
		if (!field) {
			return false;
		}

		return field.value.trim().length < 2;
	}

	setEmailFieldInvalidState(field, shouldBeInvalid) {
		if (!field) {
			return;
		}

		const emailField = field.closest('.gfield');
		field.classList.toggle('is-invalid-required', shouldBeInvalid);
		field.setAttribute('aria-invalid', shouldBeInvalid ? 'true' : 'false');
		if (emailField) {
			emailField.classList.toggle('gfield--email-required-error', shouldBeInvalid);
			this.renderEmailValidationMessage(emailField, shouldBeInvalid);
		}
	}

	renderEmailValidationMessage(emailField, shouldBeInvalid) {
		if (!emailField) {
			return;
		}

		const messageSelector = '.atr-email-validation-message, .validation_message, .gfield_validation_message';
		let messageNode = emailField.querySelector(messageSelector);

		if (!shouldBeInvalid) {
			if (messageNode && messageNode.classList.contains('atr-email-validation-message')) {
				messageNode.remove();
			}
			return;
		}

		if (!messageNode) {
			messageNode = document.createElement('div');
			messageNode.className = 'atr-email-validation-message';
			emailField.appendChild(messageNode);
		}

		messageNode.textContent = 'Invalid email';
		messageNode.style.setProperty('color', '#ea9999', 'important');
		messageNode.style.setProperty('display', 'block', 'important');
	}

	isEmailFieldRequired(field) {
		if (!field) {
			return false;
		}

		const gfield = field.closest('.gfield');
		return field.required || field.getAttribute('aria-required') === 'true' || (gfield && gfield.classList.contains('gfield_contains_required'));
	}

	isEmailFieldInvalid(field) {
		if (!field) {
			return false;
		}

		const isRequired = this.isEmailFieldRequired(field);
		const hasValue = Boolean(field.value && field.value.trim());

		if (isRequired && !hasValue) {
			return true;
		}

		if (!hasValue) {
			return false;
		}

		// HTML5 native check (basic format)
		if (!field.checkValidity()) {
			return true;
		}

		// Stricter TLD check: the final label must be 2-13 letters and must not
		// contain 3+ consecutive identical characters (catches test@test.commmmm).
		const tldMatch = field.value.trim().match(/\.([a-zA-Z]{2,13})$/);
		if (!tldMatch) return true;
		if (/(.)\1{2}/.test(tldMatch[1])) return true;

		return false;
	}

	/**
	 * Reads a Gravity Forms field's current value straight from the DOM by
	 * its numeric field id, checking a checked radio/checkbox first before
	 * falling back to a plain input/textarea value.
	 */
	getGfFieldRawValue(form, fieldId) {
		if (!form) {
			return '';
		}

		const name = `input_${fieldId}`;
		const checked = form.querySelector(`[name="${name}"]:checked`);
		if (checked) {
			return checked.value.trim();
		}

		const field = form.querySelector(`[name="${name}"]`);
		return field && field.value ? field.value.trim() : '';
	}

	/**
	 * Adults + teens + children + infants, per the "who is travelling" step.
	 * Field ids come from GetInTouchForm.php's fieldMapping().
	 */
	getPaxCount(form) {
		return [55, 56, 57, 58, 59].reduce((total, fieldId) => {
			const value = parseInt(this.getGfFieldRawValue(form, fieldId), 10);
			return total + (Number.isFinite(value) ? value : 0);
		}, 0);
	}

	/**
	 * Buckets the selected budget-per-person value into the LOW..TOP
	 * position label GTM expects. The field's raw value is whatever a
	 * HubSpot-synced enum property returns, which may be a "k"-shorthand
	 * range ("5-10k") or a plain absolute number - so this normalizes on
	 * the range's lower bound rather than matching exact strings.
	 */
	mapBudgetToPosition(rawValue) {
		if (!rawValue) {
			return '';
		}

		const match = rawValue.toString().match(/(\d+(?:\.\d+)?)/);
		if (!match) {
			return '';
		}

		let lowerBound = parseFloat(match[1]);
		if (lowerBound < 1000) {
			lowerBound *= 1000;
		}

		const thresholds = [
			[50000, 'TOP'],
			[20000, 'VERY_HIGH'],
			[10000, 'HIGH'],
			[5000, 'MED'],
			[2500, 'MED_LOW'],
			[1000, 'LOW'],
		];

		const bucket = thresholds.find(([threshold]) => lowerBound >= threshold);
		return bucket ? bucket[1] : 'LOW';
	}

	/**
	 * Pushes the quote wizard's conversion inputs to the dataLayer on final
	 * submit (869f245xc), and mirrors them to localStorage so GTM can still
	 * read them if the confirmation lands on a fresh pageview.
	 */
	pushQuoteConversionData(form, enquiryId) {
		if (!form) {
			return;
		}

		window.dataLayer = window.dataLayer || [];

		const alreadyPushed = window.dataLayer.some((event) => event && event.event === 'quote_form_submitted' && event.enquiryId === enquiryId);
		if (alreadyPushed) {
			return;
		}

		const budgetSelected = this.getGfFieldRawValue(form, 30);

		const conversionData = {
			conversionDestination: this.getGfFieldRawValue(form, 53),
			conversionMonth: this.getGfFieldRawValue(form, 11),
			conversionTripLength: this.getGfFieldRawValue(form, 15),
			conversionPAX: this.getPaxCount(form),
			conversionCurrency: this.getGfFieldRawValue(form, 29),
			conversionBudgetSelected: budgetSelected,
			conversionPosition: this.mapBudgetToPosition(budgetSelected),
			conversionCustomerType: this.getGfFieldRawValue(form, 50),
			conversionCall: this.getGfFieldRawValue(form, 64).toLowerCase() === 'yes',
			conversionSecondaryMessage: this.getGfFieldRawValue(form, 48),
			first_name: this.getGfFieldRawValue(form, 40),
			surname: this.getGfFieldRawValue(form, 41),
			email: this.getGfFieldRawValue(form, 42),
			// Field 72 is the raw phone input, but the intl-phone plugin renames it
			// to "input_72_phone" on init - field 98 is the hidden field it keeps in
			// sync with the combined dial code + number, so it's the reliable one to
			// read here regardless of which phone input (primary/secondary) was used.
			phone_number: this.getGfFieldRawValue(form, 98),
		};

		try {
			localStorage.setItem('atr_quote_conversion_data', JSON.stringify(conversionData));
		} catch (error) {
			// Storage can be unavailable (private browsing, quota) - the
			// dataLayer push below still carries the data for GTM.
		}

		window.dataLayer.push({
			event: 'quote_form_submitted',
			enquiryId: enquiryId,
			...conversionData,
		});
	}

	getCurrentVisiblePage(form, formId) {
		if (!form) {
			return null;
		}

		const sourcePageInput = form.querySelector(`#gform_source_page_number_${formId}`);
		const currentPageNumber = sourcePageInput ? sourcePageInput.value : null;
		const pageFromSource = currentPageNumber ? form.querySelector(`#gform_page_${formId}_${currentPageNumber}`) : null;

		if (pageFromSource) {
			return pageFromSource;
		}

		const allPages = form.querySelectorAll('.gform_page');
		for (const page of allPages) {
			if (window.getComputedStyle(page).display !== 'none') {
				return page;
			}
		}

		return null;
	}

	getRequiredPhoneFields(page) {
		if (!page) {
			return [];
		}

		return Array.from(page.querySelectorAll('.ginput_container_phone input[type="tel"]')).filter((field) => this.isPhoneFieldRequired(field));
	}

	getPagePhoneFields(page) {
		if (!page) {
			return [];
		}

		return Array.from(page.querySelectorAll('.ginput_container_phone input[type="tel"]'));
	}

	getActivePhoneInput(page) {
		if (!page) {
			return null;
		}

		const pagePhoneInputs = Array.from(page.querySelectorAll('.ginput_container_phone input[type="tel"]'));
		if (!pagePhoneInputs.length) {
			return null;
		}

		return pagePhoneInputs.find((input) => input.value && input.value.trim() !== '') || pagePhoneInputs[0];
	}

	syncPhoneFieldBeforeSubmit(field, { dispatchChange = true } = {}) {
		if (!field) {
			return;
		}

		const phoneContainer = field.closest('.ginput_container_phone');
		const hiddenInputs = phoneContainer ? Array.from(phoneContainer.querySelectorAll('input[type="hidden"]')) : [];
		const hiddenPhoneInput = hiddenInputs.find((input) => !input.name.endsWith('_country_code'));
		const hiddenCountryInput = hiddenInputs.find((input) => input.name.endsWith('_country_code'));
		const itiInstance =
			typeof window !== 'undefined' && window.intlTelInputGlobals && typeof window.intlTelInputGlobals.getInstance === 'function'
				? window.intlTelInputGlobals.getInstance(field)
				: null;
		const selectedCountryData = itiInstance && typeof itiInstance.getSelectedCountryData === 'function' ? itiInstance.getSelectedCountryData() : null;
		const selectedDialCode =
			selectedCountryData && selectedCountryData.dialCode
				? `+${selectedCountryData.dialCode}`
				: phoneContainer?.querySelector('.iti__selected-dial-code')?.textContent?.trim() || '';
		const selectedCountryIso = (selectedCountryData?.iso2 || hiddenCountryInput?.value || '').toLowerCase();
		const numericValue = field.value ? field.value.trim() : '';

		if (numericValue && hiddenPhoneInput) {
			hiddenPhoneInput.value = `${selectedDialCode}${numericValue}`;
		}
		if (hiddenCountryInput && selectedCountryIso) {
			hiddenCountryInput.value = selectedCountryIso;
		}

		if (dispatchChange) {
			field.dispatchEvent(new Event('change', { bubbles: true }));
		}
	}

	init() {
		this.isLoading = false;
		this.watchdogBound = false;
		this.defaultImageUrl = this.getDefaultImageUrl();
		this.currentImage = this.defaultImageUrl;
		this.configureReviewData();
		this.refreshReviewForCurrentState();

		window.addEventListener('pegasusformlocationupdate', (e) => {
			const nextImage = this.buildImageUrl(e.detail);
			if (nextImage !== this.currentImage) {
				this.imageLoaded = false;
				this.currentImage = nextImage;
			}

			this.refreshReviewForCurrentState();
		});

		document.addEventListener('gform/post_init', (event) => {
			this.getImageUpdates();
			this.refreshReviewForCurrentState();
			this.handleDateFields();
			this.addChildrenGuestsSelector();
			this.handlePhoneNumberInput();
			this.handleNameInputFields();
			this.handleEmailInputFields();
			this.handleCallDateFields();
			this.setupRouteSelector();

			this.isLoading = false;

			const formId = event.detail.formId;

			this.bindRecoveryWatchdog(formId);

			const enquiryId = document.body.getAttribute('data-enquiry-id');
			const enquiryInputField = this.$el.querySelector(`input[id="input_${formId}_120"]`);

			if (enquiryInputField) {
				enquiryInputField.value = enquiryId;
			}

			const _this = this;

			if (!this.boundValidationHandlers.has(formId)) {
				document.addEventListener(
					'click',
					function (e) {
						const isNextButton = e.target && e.target.matches && e.target.matches(`input.gform_next_button[id^="gform_next_button_${formId}_"]`);
						const isSubmitButton =
							e.target &&
							e.target.matches &&
							e.target.matches(
								`input.gform_button[type="submit"][id^="gform_submit_button_${formId}"], button.gform_button[type="submit"][id^="gform_submit_button_${formId}"]`,
							);

						if (isNextButton || isSubmitButton) {
							const form = e.target.closest('form');
							if (!form) {
								return;
							}

							if (_this.callBookingPhoneInvalid(e.target)) {
								e.stopImmediatePropagation();
								e.preventDefault();
								return;
							}

							// Most reliable source: the clicked Next button sits inside the active page footer.
							const currentPageFromButton = e.target.closest('.gform_page');
							const currentPage = currentPageFromButton || _this.getCurrentVisiblePage(form, formId);

							// check current page fields are valid
							const currentPageFields = currentPage
								? currentPage.querySelectorAll(
										'.gfield input[aria-required="true"], .gfield select[aria-required="true"], .gfield textarea[aria-required="true"]',
									)
								: [];

							let isValid = true;

							currentPageFields.forEach((field) => {
								if (field && !field.value.trim()) {
									isValid = false;
								}

								if (field && field.getAttribute('type') === 'tel') {
									const phoneIsInvalid = _this.isPhoneFieldInvalid(field);
									_this.setPhoneFieldInvalidState(field, phoneIsInvalid);

									if (phoneIsInvalid) {
										isValid = false;
									}
								}

								if (field && field.getAttribute('type') === 'email') {
									const emailIsInvalid = _this.isEmailFieldInvalid(field);
									_this.setEmailFieldInvalidState(field, emailIsInvalid);

									if (emailIsInvalid) {
										isValid = false;
									}
								}

								if (field && (field.name === 'input_40' || field.name === 'input_41')) {
									const nameIsInvalid = _this.isNameFieldInvalid(field);
									_this.setNameFieldInvalidState(field, nameIsInvalid);

									if (nameIsInvalid) {
										isValid = false;
									}
								}
							});

							// Validate all phone fields on the current page:
							// - required + empty => invalid
							// - optional + empty => valid
							// - any non-empty with plugin error => invalid
							const pagePhoneFields = _this.getPagePhoneFields(currentPage);

							pagePhoneFields.forEach((field) => {
								_this.syncPhoneFieldBeforeSubmit(field);
								const phoneIsInvalid = _this.isPhoneFieldInvalid(field);
								_this.setPhoneFieldInvalidState(field, phoneIsInvalid);
								if (phoneIsInvalid) {
									isValid = false;
								}
							});

							if (!isValid) {
								e.stopImmediatePropagation();
								e.preventDefault();
								return;
							}

							// Show loading overlay on every valid Next/Submit transition so users
							// see feedback and don't double-click (which logs "aborting" and
							// can leave gf_submitting stuck). gform/post_init resets isLoading=false
							// once the new page renders; the watchdog clears it on PHP timeout.
							_this.isLoading = true;
							// Start watchdog grace period from this click, not from component init.
							_this.lastSubmitAt = Date.now();

							if (e.target.id === `gform_next_button_${formId}_46`) {
								window.dataLayer = window.dataLayer || [];

								// If enquiryId already exists in the data layer don't push it again
								const existingEnquiryEvent = window.dataLayer.find((event) => event.event === 'enquiry_submitted' && event.enquiryId === enquiryId);

								if (!existingEnquiryEvent) {
									window.dataLayer.push({
										event: 'enquiry_submitted',
										enquiryId: enquiryId,
									});
								}
							}

							if (isSubmitButton) {
								_this.pushQuoteConversionData(form, enquiryId);
							}
						}
					},
					true,
				);
				this.boundValidationHandlers.add(formId);
			}

			const childrenRadioGroup = this.$el.querySelectorAll('.children-selector input[type="radio"][name="input_23"]');

			if (!childrenRadioGroup || childrenRadioGroup.length === 0) {
				return;
			}

			childrenRadioGroup.forEach((radio) => {
				const radioLabel = radio.nextElementSibling;
				radioLabel.addEventListener('click', (event) => {
					this.$nextTick(() => {
						this.addChildrenGuestsSelector(true);
					});
				});
			});
		});

		document.addEventListener('gform/page_loaded', () => {
			this.refreshReviewForCurrentState();
			this.syncCloseButtonInlineWithHeading();
			this.handleEmailInputFields();
			this.handleNameInputFields();
			this.handlePhoneNumberInput();
		});

		document.addEventListener('gform/post_render', () => {
			this.refreshReviewForCurrentState();
			this.syncCloseButtonInlineWithHeading();
			this.ensureConfirmationFooter();
		});

		if (typeof jQuery !== 'undefined') {
			jQuery(document).on('gform_confirmation_loaded', () => {
				this.syncCloseButtonInlineWithHeading();
				this.ensureConfirmationFooter();
			});
		}

		window.addEventListener('resize', () => {
			this.syncCloseButtonInlineWithHeading();
		});

		this.syncCloseButtonInlineWithHeading();
		this.ensureConfirmationFooter();
	}

	/**
	 * Recovers the form when Gravity Forms' `gf_submitting_{formId}` flag gets
	 * stuck (e.g. a mobile browser kills an in-flight AJAX submit when the tab
	 * is backgrounded), which otherwise silently swallows every next/previous
	 * submit. Bound once per component.
	 */
	bindRecoveryWatchdog(formId) {
		if (this.watchdogBound) {
			return;
		}
		this.watchdogBound = true;

		this.lastSubmitAt = Date.now();

		const recoverIfStuck = () => {
			const isSubmitting = window['gf_submitting_' + formId] === true;

			if (isSubmitting && Date.now() - this.lastSubmitAt > 10000) {
				// Grace period elapsed on a stuck flag: the submission was
				// interrupted. Safe to clear and re-enable navigation.
				window['gf_submitting_' + formId] = false;
				this.isLoading = false;
			} else if (!isSubmitting) {
				// Nothing in flight; just make sure the overlay isn't stuck up.
				this.isLoading = false;
			}
			// If submitting and still within the 10s grace period, do nothing so
			// a legitimately slow in-flight submission is never interrupted
			// (clearing too early risks a duplicate HubSpot submission).
		};

		// Measure the grace period from the last actual submit.
		document.addEventListener(
			'submit',
			(e) => {
				if (e.target && e.target.id === 'gform_' + formId) {
					this.lastSubmitAt = Date.now();
				}
			},
			true,
		);

		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				setTimeout(recoverIfStuck, 1500);
			}
		});

		window.addEventListener('pageshow', (event) => {
			if (event.persisted) {
				recoverIfStuck();
			}
		});

		window.addEventListener('online', () => {
			setTimeout(recoverIfStuck, 1500);
		});

		// Run unconditionally: the stuck `gf_submitting_{formId}` flag can persist
		// on any page transition (not just the final-next one that sets
		// `isLoading`), so gating this on `isLoading` left generic page
		// transitions unable to self-heal. `recoverIfStuck` is already safe to
		// call at any time — it only clears the flag after the 10s grace.
		setInterval(recoverIfStuck, 5000);
	}

	ensureConfirmationFooter() {
		const confirmationWrapper = this.$root.querySelector('#gform_confirmation_wrapper_12');

		if (!confirmationWrapper) {
			return;
		}

		const existingFooter = confirmationWrapper.querySelector('.gform-confirmation-footer');
		if (existingFooter) {
			return;
		}

		const footer = document.createElement('div');
		footer.className = 'gform-page-footer gform-footer gform-confirmation-footer top_label';
		footer.setAttribute('aria-hidden', 'true');

		const spacer = document.createElement('div');
		spacer.className = 'gform-confirmation-footer__spacer';
		footer.appendChild(spacer);

		confirmationWrapper.appendChild(footer);
	}

	syncCloseButtonInlineWithHeading() {
		const closeWrap = this.$root.querySelector('.get-in-touch-close-wrap');
		const closeButtonTemplate = closeWrap?.querySelector('.SearchSentence__Close');
		const form = this.$root.querySelector('form');

		if (!closeWrap || !closeButtonTemplate) {
			return;
		}

		const isDesktop = window.matchMedia('(min-width: 768px)').matches;
		const confirmationSection = this.$root.querySelector('.gform_confirmation_wrapper .gfield--type-section');
		const visiblePage = form ? Array.from(form.querySelectorAll('.gform_page')).find((page) => window.getComputedStyle(page).display !== 'none') : null;
		const pageSection = visiblePage
			? Array.from(visiblePage.querySelectorAll('.gfield--type-section')).find((section) => window.getComputedStyle(section).display !== 'none')
			: null;
		const targetSection = confirmationSection || pageSection;

		this.$root.querySelectorAll('.get-in-touch-inline-close-clone').forEach((cloneButton) => {
			cloneButton.remove();
		});

		this.$root.querySelectorAll('.gfield--with-inline-close').forEach((section) => {
			if (section !== targetSection) {
				section.classList.remove('gfield--with-inline-close');
			}
		});

		if (!isDesktop || !targetSection) {
			return;
		}

		const inlineCloseClone = closeButtonTemplate.cloneNode(true);
		inlineCloseClone.classList.add('get-in-touch-inline-close', 'get-in-touch-inline-close-clone');
		inlineCloseClone.addEventListener('click', (event) => {
			event.preventDefault();
			this.goToLastPage();
		});

		targetSection.classList.add('gfield--with-inline-close');
		targetSection.appendChild(inlineCloseClone);
	}

	onImageLoad() {
		this.imageLoaded = true;
	}

	configureReviewData() {
		if (!this.$root) {
			return;
		}

		const fallback = {
			text: "ATR are experts in their field and I wouldn't trust anyone else.",
			name: 'Tara Stewart',
		};

		const reviewConfigRaw = this.$root.dataset.reviewConfig || '{}';

		try {
			const parsedConfig = JSON.parse(reviewConfigRaw);
			const reviewConfig = parsedConfig && typeof parsedConfig === 'object' ? parsedConfig : {};
			const globalDefault = reviewConfig.globalDefault || {};

			this.defaultReview = {
				text: typeof globalDefault.text === 'string' && globalDefault.text.trim() !== '' ? globalDefault.text.trim() : fallback.text,
				name: typeof globalDefault.name === 'string' && globalDefault.name.trim() !== '' ? globalDefault.name.trim() : fallback.name,
			};

			this.locationDefaultReviewMap =
				reviewConfig.locationDefaultMap && typeof reviewConfig.locationDefaultMap === 'object' ? reviewConfig.locationDefaultMap : {};
			this.locationStepReviewMap = reviewConfig.locationStepMap && typeof reviewConfig.locationStepMap === 'object' ? reviewConfig.locationStepMap : {};
		} catch (error) {
			this.defaultReview = fallback;
			this.locationDefaultReviewMap = {};
			this.locationStepReviewMap = {};
		}

		this.currentReviewText = this.defaultReview.text;
		this.currentReviewerName = this.defaultReview.name;
	}

	normalizeReviewLocationKey(location) {
		if (!location) {
			return '';
		}

		return location.toString().replaceAll('-', ' ').trim().toLowerCase();
	}

	normalizeReviewStepKey(step) {
		const parsedStep = parseInt(step, 10);
		return Number.isFinite(parsedStep) && parsedStep > 0 ? String(parsedStep) : null;
	}

	isValidReview(review) {
		return review && typeof review.text === 'string' && review.text.trim() !== '' && typeof review.name === 'string' && review.name.trim() !== '';
	}

	getCurrentSelectedLocation() {
		const form = this.$root ? this.$root.querySelector('form') : null;
		if (!form) {
			return '';
		}

		const checkedLocation = form.querySelector('input[name="input_53"]:checked');
		if (checkedLocation && checkedLocation.value) {
			return checkedLocation.value.toString();
		}

		const formData = new FormData(form);
		const values = formData.getAll('input_53');
		return values.length > 0 ? values[0].toString() : '';
	}

	resolveReviewForLocationAndStep(location, step) {
		const normalizedLocation = this.normalizeReviewLocationKey(location);
		const normalizedStep = this.normalizeReviewStepKey(step);
		const locationStepKey = normalizedLocation && normalizedStep ? `${normalizedLocation}|${normalizedStep}` : null;

		const locationStepReview = locationStepKey ? this.locationStepReviewMap[locationStepKey] : null;
		if (this.isValidReview(locationStepReview)) {
			return {
				text: locationStepReview.text.trim(),
				name: locationStepReview.name.trim(),
			};
		}

		const locationDefaultReview = normalizedLocation ? this.locationDefaultReviewMap[normalizedLocation] : null;
		if (this.isValidReview(locationDefaultReview)) {
			return {
				text: locationDefaultReview.text.trim(),
				name: locationDefaultReview.name.trim(),
			};
		}

		return this.defaultReview;
	}

	applyReviewForState(location, step) {
		const review = this.resolveReviewForLocationAndStep(location, step);
		this.currentReviewText = review.text;
		this.currentReviewerName = review.name;
	}

	refreshReviewForCurrentState() {
		const location = this.getCurrentSelectedLocation();
		const step = this.getCurrentPageStep();
		this.applyReviewForState(location, step);
	}

	getImageBaseUrl() {
		if (this.$root && this.$root.dataset.imageBaseUrl) {
			return this.$root.dataset.imageBaseUrl;
		}

		return 'https://cdn.atrsafari.com/cdn/inquiry/0/';
	}

	getThemeFallbackUrl() {
		if (!this.currentLocationSlug) {
			return this.defaultImageUrl;
		}

		return `/wp-content/themes/pegasus/assets/img/form-images/locations/${this.currentLocationSlug}.jpg`;
	}

	getDefaultImageUrl() {
		if (this.$root && this.$root.dataset.defaultImageUrl) {
			const configuredImage = this.$root.dataset.defaultImageUrl.trim();
			if (configuredImage) {
				return configuredImage;
			}
		}

		return '/wp-content/themes/pegasus/assets/img/form-images/yellow-canyon.jpg';
	}

	getCurrentPageStep() {
		const stepElement = this.$root.querySelector('.gf_step_current_page');

		if (!stepElement) {
			return 1;
		}

		const step = parseInt(stepElement.textContent.trim(), 10);

		return Number.isFinite(step) && step > 0 ? step : 1;
	}

	isVideoIntroductionEnabled(urlParams = new URLSearchParams(window.location.search)) {
		const value = (urlParams.get('video_introduction') || '').toString().trim().toLowerCase();
		return value === 'true' || value === '1' || value === 'show video introduction';
	}

	updateVideoIntroductionUrlParam(isEnabled) {
		const url = new URL(window.location.href);
		if (isEnabled) {
			url.searchParams.set('video_introduction', true);
		} else {
			url.searchParams.delete('video_introduction');
		}
		history.replaceState({}, '', url.toString());
	}

	disableVideoIntroductionMode() {
		const checkbox = this.$el.querySelector('.video-introduction-toggle input[type="checkbox"]');
		if (checkbox) {
			checkbox.checked = false;
			checkbox.dispatchEvent(new Event('change', { bubbles: true }));
			checkbox.dispatchEvent(new Event('input', { bubbles: true }));
		}
		this.updateVideoIntroductionUrlParam(false);
	}

	buildImageUrl(locationSlug) {
		if (!locationSlug) {
			return this.currentImage;
		}

		const locationString = locationSlug.toString();

		const themeSlug = locationString.replaceAll(' ', '-');
		this.currentLocationSlug = themeSlug;

		const cdnSlug = themeSlug.toLowerCase();
		const step = this.getCurrentPageStep();
		const stepString = String(step).padStart(2, '0');

		const baseUrl = this.getImageBaseUrl();

		return `${baseUrl}${cdnSlug}/${stepString}-image.jpg`;
	}

	/**
	 * Sync the visible .route-selector radios with the hidden GF radio field (input_141).
	 * When a visible radio is changed, the matching hidden GF radio is checked and
	 * Gravity Forms' own change event is fired so its conditional logic picks it up.
	 */
	setupRouteSelector() {
		const routeSelector = this.$el.querySelector('.route-selector');
		const videoIntroTriggers = routeSelector?.querySelectorAll('button[data-video-intro="true"]') || [];
		const form = this.$el.querySelector('form');
		const routeStorageKey = form?.id ? `${form.id}_selected_route` : 'selected_route';
		const getPageNumberFromId = (pageId = '') => {
			const match = pageId.match(/_(\d+)$/);
			return match ? parseInt(match[1], 10) : null;
		};
		const syncHiddenRouter = (route) => {
			if (!route) return null;

			const hiddenRouterCandidates = this.$el.querySelectorAll('.hidden-router-js input[type="radio"]');
			hiddenRouterCandidates.forEach((candidate) => {
				candidate.disabled = false;
			});

			const gfRadio = Array.from(hiddenRouterCandidates).find((r) => r.value === route) || null;

			if (!gfRadio) {
				return null;
			}

			gfRadio.checked = true;
			gfRadio.dispatchEvent(new Event('change', { bubbles: true }));
			gfRadio.dispatchEvent(new Event('input', { bubbles: true }));
			return gfRadio;
		};

		const setVideoIntroductionMode = ({ shouldUpdateUrl } = { shouldUpdateUrl: true }) => {
			const checkbox = this.$el.querySelector('.video-introduction-toggle input[type="checkbox"]');
			if (checkbox) {
				checkbox.checked = true;
				checkbox.dispatchEvent(new Event('change', { bubbles: true }));
				checkbox.dispatchEvent(new Event('input', { bubbles: true }));
			}

			if (!shouldUpdateUrl) return;
			this.updateVideoIntroductionUrlParam(true);
		};

		videoIntroTriggers.forEach((trigger) => {
			if (trigger.dataset.videoRouteBound === 'true') {
				return;
			}

			trigger.dataset.videoRouteBound = 'true';

			trigger.addEventListener('click', (event) => {
				event.preventDefault();
				setVideoIntroductionMode({ shouldUpdateUrl: true });
			});
		});

		const urlParams = new URLSearchParams(window.location.search);
		if (this.isVideoIntroductionEnabled(urlParams)) {
			setVideoIntroductionMode({ shouldUpdateUrl: false });
		}

		if (form && form.dataset.videoIntroBackBound !== 'true') {
			form.dataset.videoIntroBackBound = 'true';
			form.addEventListener(
				'click',
				(event) => {
					const previousButton = event.target && event.target.closest && event.target.closest('input.gform_previous_button, button.gform_previous_button');

					if (!previousButton || !form.contains(previousButton)) {
						return;
					}

					const videoIntroToggle = this.$el.querySelector('.video-introduction-toggle input[type="checkbox"]');
					if (!videoIntroToggle || !videoIntroToggle.checked) {
						return;
					}

					event.preventDefault();
					event.stopImmediatePropagation();
					this.disableVideoIntroductionMode();
				},
				true,
			);
		}

		const visibleRadios = this.$el.querySelectorAll('.route-selector input[type="radio"][data-route]');

		if (!visibleRadios.length) return;

		// Hide the next button on whichever page contains the route-selector,
		// so it can still be triggered programmatically but not clicked by the user.
		const routePage = this.$el.querySelector('.route-selector')?.closest('.gform_page');
		const routePageNextBtn = routePage?.querySelector('.gform-page-footer .gform_next_button');
		if (routePageNextBtn) {
			routePageNextBtn.style.opacity = '0';
			routePageNextBtn.style.pointerEvents = 'none';
			routePageNextBtn.style.position = 'absolute';
		}

		visibleRadios.forEach((radio) => {
			// Guard prevents duplicate listeners accumulating on every gform/post_init.
			// Without this, after N page transitions each radio has N change handlers,
			// firing N auto-advance clicks simultaneously when the user picks a route.
			if (radio.dataset.routeListenerBound === 'true') return;
			radio.dataset.routeListenerBound = 'true';
			radio.addEventListener('change', () => {
				if (!radio.checked) return;

				const route = radio.dataset.route;
				localStorage.setItem(routeStorageKey, route);
				const gfRadio = syncHiddenRouter(route);

				if (!gfRadio) return;

				// If "call" selected, set the "would you like a call?" field to Yes
				if (route === 'call') {
					const callYesRadio = this.$el.querySelector('input[name="input_64"][value="Yes"]');
					if (callYesRadio) {
						callYesRadio.checked = true;
						callYesRadio.dispatchEvent(new Event('change', { bubbles: true }));
						callYesRadio.dispatchEvent(new Event('input', { bubbles: true }));
					}
				}

				// Auto-advance to next page for "call" and "enquiry" routes
				if (route === 'call' || route === 'enquiry') {
					setTimeout(() => {
						const formId = form?.id ? form.id.replace('gform_', '') : '';
						const sourcePageInput = formId ? this.$el.querySelector(`#gform_source_page_number_${formId}`) : null;
						const currentPageNumber = sourcePageInput?.value || null;
						const currentPage = currentPageNumber ? this.$el.querySelector(`#gform_page_${formId}_${currentPageNumber}`) : null;
						const visiblePage =
							currentPage || Array.from(this.$el.querySelectorAll('.gform_page')).find((page) => window.getComputedStyle(page).display !== 'none');
						const nextButton =
							visiblePage?.querySelector('.gform-page-footer .gform_next_button') || routePage?.querySelector('.gform-page-footer .gform_next_button');

						if (nextButton) {
							const targetPageInput = formId ? this.$el.querySelector(`#gform_target_page_number_${formId}`) : null;
							void targetPageInput;
							nextButton.click();
						}
					}, 100);
				}
			});
		});

		// Re-apply route before any Next/Submit click so GF paging/conditional logic sees it.
		if (form && form.dataset.routePersistBound !== 'true') {
			form.dataset.routePersistBound = 'true';
			form.addEventListener(
				'click',
				(event) => {
					const actionButton =
						event.target &&
						event.target.closest &&
						event.target.closest(
							'input.gform_next_button, button.gform_next_button, input.gform_previous_button, button.gform_previous_button, input.gform_button[type="submit"], button.gform_button[type="submit"]',
						);

					if (!actionButton || !form.contains(actionButton)) {
						return;
					}

					const persistedRoute = localStorage.getItem(routeStorageKey);
					if (!persistedRoute) {
						return;
					}

					const syncedRadio = syncHiddenRouter(persistedRoute);
					void syncedRadio;

					const isPreviousButton = actionButton.matches('input.gform_previous_button, button.gform_previous_button');
					if (!isPreviousButton || persistedRoute !== 'call') {
						return;
					}

					const formId = form.id ? form.id.replace('gform_', '') : '';
					const sourcePageInput = formId ? this.$el.querySelector(`#gform_source_page_number_${formId}`) : null;
					const targetPageInput = formId ? this.$el.querySelector(`#gform_target_page_number_${formId}`) : null;
					const skipPageElement = this.$el.querySelector('.gform_page[data-js="page-field-id-46"]');
					const skipPageNumber = skipPageElement ? getPageNumberFromId(skipPageElement.id) : null;
					const currentPage = sourcePageInput ? parseInt(sourcePageInput.value, 10) : NaN;
					const defaultPreviousTarget = Number.isFinite(currentPage) ? Math.max(currentPage - 1, 1) : null;

					if (!targetPageInput || !skipPageNumber || !defaultPreviousTarget) {
						return;
					}

					// If Back would land on the "Can we call you?" page, skip it.
					if (defaultPreviousTarget === skipPageNumber && skipPageNumber > 1) {
						const adjustedTarget = skipPageNumber - 1;
						targetPageInput.value = String(adjustedTarget);
					}
				},
				true,
			);
		}

		// On init, mirror any already-checked visible radio (e.g. after back navigation).
		// Only fire when the route selector page is currently visible — querying this.$el
		// finds checked radios on hidden GF pages too, which would trigger a silent
		// auto-advance on every page transition after the route has been picked.
		const routeSelectorPage = this.$el.querySelector('.route-selector')?.closest('.gform_page');
		const routeSelectorPageIsVisible = routeSelectorPage
			? window.getComputedStyle(routeSelectorPage).display !== 'none'
			: false;
		const checkedOnLoad = routeSelectorPageIsVisible
			? this.$el.querySelector('.route-selector input[type="radio"][data-route]:checked')
			: null;
		if (checkedOnLoad) {
			checkedOnLoad.dispatchEvent(new Event('change', { bubbles: true }));
		} else {
			const persistedRoute = localStorage.getItem(routeStorageKey);
			if (persistedRoute) {
				syncHiddenRouter(persistedRoute);
			}
		}
	}

	goBackToForm(formId) {
		var prevPage = localStorage.getItem('gf_prev_page_' + formId) || 1;
		var formUrl = window.location.href.split('?')[0] + '?gf_page=' + prevPage;
		window.location.href = formUrl;
	}

	addChildrenGuestsSelector() {
		const childRadioElements = this.$root.querySelectorAll('.children-selector input[type="radio"][name="input_23"]');
		const addChildrenRadio = this.$root.querySelector('.children-selector input[type="radio"][name="input_23"][value="Add children"]');

		if (!childRadioElements || childRadioElements.length === 0 || !addChildrenRadio || !addChildrenRadio.parentElement) {
			return;
		}

		const addChildrenSelector = addChildrenRadio.parentElement;

		childRadioElements.forEach((radio) => {
			if (radio.value === 'Add children' && radio.checked) {
				this.showChildrenGuestsSelector = true;
			}
		});

		if (this.showChildrenGuestsSelector) {
			addChildrenSelector.style.display = 'none';
		} else {
			addChildrenSelector.style.display = '';
		}
	}

	handleNumberFields() {
		this.$root.querySelectorAll('.ginput_container_number').forEach((input) => {
			const number = input.querySelector('input[type="number"]');
			//set the number value to the minimum value
			input.addEventListener('click', function (event) {
				const rect = this.getBoundingClientRect();
				const clickX = event.clientX - rect.left;

				// Check if the click is on the left or right of the input
				if (clickX < 30) {
					number.stepDown(1);
					// number.value = number.value;
				} else if (clickX > rect.width - 30) {
					// Increment (right side)
					number.stepUp(1);
					// number.value = number.value;
				}

				// Trigger change event
				number.dispatchEvent(new Event('change', { bubbles: true }));
				number.dispatchEvent(
					new CustomEvent('gform_input_change', {
						bubbles: true,
						detail: { field: number },
					}),
				);
			});
		});
	}

	getImageUpdates() {
		const form = this.$root.querySelector('form');

		if (!form) {
			return;
		}

		// Attach live change listeners so the image updates immediately on selection
		const locationRadios = form.querySelectorAll('input[name="input_53"]');
		locationRadios.forEach((radio) => {
			if (!radio.dataset.imageListenerAttached) {
				radio.dataset.imageListenerAttached = 'true';
				radio.addEventListener('change', () => {
					if (radio.checked) {
						window.dispatchEvent(
							new CustomEvent('pegasusformlocationupdate', {
								detail: radio.value.replaceAll(' ', '-'),
							}),
						);
					}
				});
			}
		});

		// Mirror the already-selected value on page load / back navigation
		const formData = new FormData(form);
		const locationField = formData.getAll('input_53');

		if (locationField.length === 0) {
			this.refreshReviewForCurrentState();
			return;
		}

		const selectedLocation = locationField[0];
		const locationSlug = selectedLocation ? selectedLocation.toString() : '';
		this.refreshReviewForCurrentState();

		const nextImage = this.buildImageUrl(locationSlug);

		if (nextImage === this.currentImage) {
			return;
		}

		this.imageLoaded = false;
		this.currentImage = nextImage;
	}

	handleDateFields() {
		let checkedMonthRadio = this.$root.querySelector('.gfield input[type="radio"][name="input_11"]:checked');
		const monthRadios = this.$root.querySelectorAll('.gfield input[type="radio"][name="input_11"]');

		if (!monthRadios || monthRadios.length === 0) {
			return;
		}

		const date = new Date();
		const currentYear = date.getFullYear().toString().slice(-2);

		const moreMonths = this.$root.querySelector('.more-months');

		if (!moreMonths) {
			this.hideMonths();
			this.updateMonths();
			return;
		}

		if (checkedMonthRadio && parseInt(this.getDateArray(checkedMonthRadio)[1]) >= parseInt(currentYear) + 2) {
			this.showMonths();
			moreMonths.style.display = 'none';
		} else {
			this.hideMonths();
		}

		this.handleMoreMonths();

		this.updateMonths();
	}

	getMonthsHeight() {
		const monthsField = this.$root.querySelector('.months-field');

		if (!monthsField) {
			return;
		}

		const radioGroup = monthsField.querySelector('.gfield_radio');

		if (!radioGroup || radioGroup.length === 0) {
			return;
		}

		radioGroup.style.height = '';

		const radioGroupHeight = radioGroup.offsetHeight;

		return radioGroupHeight;
	}

	hideMonths() {
		const monthsField = this.$root.querySelector('.months-field');

		if (!monthsField) {
			return;
		}

		const radioGroup = monthsField.querySelector('.gfield_radio');

		if (!radioGroup || radioGroup.length === 0) {
			return;
		}

		let monthsHeight = this.getMonthsHeight();

		radioGroup.classList.add('overflow-hidden');

		radioGroup.style.height = `${monthsHeight / 2}px`;

		window.addEventListener('resize', () => {
			monthsHeight = this.getMonthsHeight();
			radioGroup.style.height = `${monthsHeight / 2}px`;
		});
	}

	showMonths() {
		const monthsField = this.$root.querySelector('.months-field');

		if (!monthsField) {
			return;
		}

		const radioGroup = monthsField.querySelector('.gfield_radio');

		if (!radioGroup || radioGroup.length === 0) {
			return;
		}

		let monthsHeight = this.getMonthsHeight();

		radioGroup.style.height = `${monthsHeight}px`;

		window.addEventListener('resize', () => {
			monthsHeight = this.getMonthsHeight();
			radioGroup.style.height = `${monthsHeight}px`;
		});
	}

	handleMoreMonths() {
		const moreMonths = this.$root.querySelector('.more-months');
		if (!moreMonths) {
			return;
		}

		const input = moreMonths.querySelector('input[type="radio"]');

		if (!input) {
			return;
		}

		if (input.checked) {
			this.showMonths();

			moreMonths.remove();
		}

		input.addEventListener('change', (event) => {
			if (input.checked) {
				this.showMonths();

				moreMonths.remove();
			}
		});
	}

	updateMonths() {
		const monthRadios = this.$root.querySelectorAll('.gfield input[type="radio"][name="input_11"]');

		if (!monthRadios || monthRadios.length === 0) {
			return;
		}

		const date = new Date();
		const currentYear = date.getFullYear().toString().slice(-2);

		// Loop through month radios and if it's value is not available for the selected year, disabled it and lower opacity otherwise enable it
		monthRadios.forEach((radio) => {
			const dateArray = this.getDateArray(radio);
			if (
				!this.isMonthAvailable(dateArray[1], dateArray[0]) ||
				(parseInt(dateArray[1]) >= parseInt(currentYear) + 3 && this.isMonthAvailable(currentYear, dateArray[0]))
			) {
				radio.classList.add('disabled');
				// radio.setAttribute('disabled', 'disabled');
			} else {
				// radio.removeAttribute('disabled');
				radio.classList.remove('disabled');
			}

			radio.addEventListener('change', (event) => {
				const yearField = this.$root.querySelector('.gfield input[type="hidden"][name="input_115"]');

				const monthField = this.$root.querySelector('.gfield input[type="hidden"][name="input_119"]');

				if (!yearField || !monthField) {
					return;
				}

				const selectedYear = '20' + dateArray[1];

				yearField.value = selectedYear;
				monthField.value = dateArray[0];
			});
		});
	}

	getDateArray(radio) {
		// Get next sibling of radio
		const label = radio.nextElementSibling;

		// get text content of label
		const date = label.textContent;

		const dateArray = date.split(' ');

		Object.entries(this.getMonths()).forEach(([key, value]) => {
			if (dateArray[0] === value) {
				dateArray[0] = key;
			}
		});

		// Split date by space
		return dateArray;
	}

	getMonths() {
		return {
			January: 'Jan',
			February: 'Feb',
			March: 'Mar',
			April: 'Apr',
			May: 'May',
			June: 'Jun',
			July: 'Jul',
			August: 'Aug',
			September: 'Sep',
			October: 'Oct',
			November: 'Nov',
			December: 'Dec',
		};
	}

	getAvailableMonthsLeftInCurrentYear() {
		let months = [];
		let currentMonth = new Date().getMonth();
		let monthsLeft = 12 - currentMonth;
		for (let i = 0; i < monthsLeft; i++) {
			months.push(Object.keys(this.getMonths())[currentMonth + i]);
		}
		return months;
	}

	isMonthAvailable(year, month) {
		const date = new Date();
		const currentYear = date.getFullYear().toString().slice(-2);
		if (year == currentYear) {
			return this.getAvailableMonthsLeftInCurrentYear().includes(month);
		}

		return true;
	}

	/**
	 * Derive a stable sessionStorage key for a phone input.
	 * The phone plugin renames input.name from e.g. "input_72" to "input_72_phone"
	 * at init time, so we normalize by stripping the "_phone" suffix to get a
	 * consistent key across re-initializations.
	 */
	getPhoneFieldStorageKey(input) {
		const raw = input.name || input.id || '';
		return raw.replace(/_phone$/, '');
	}

	/**
	 * Save phone field state (country + value) to sessionStorage so it survives
	 * GF pagination and the phone plugin's async geo-lookup re-initialization.
	 */
	persistPhoneFieldState(input) {
		if (!input) return;
		const fieldKey = this.getPhoneFieldStorageKey(input);
		if (!fieldKey) return;

		const iti =
			typeof window !== 'undefined' && window.intlTelInputGlobals && typeof window.intlTelInputGlobals.getInstance === 'function'
				? window.intlTelInputGlobals.getInstance(input)
				: null;
		const countryData = iti && typeof iti.getSelectedCountryData === 'function' ? iti.getSelectedCountryData() : null;
		const iso = (countryData?.iso2 || '').toLowerCase();
		const value = input.value ? input.value.trim() : '';

		if (iso) {
			sessionStorage.setItem('atr_phone_country_' + fieldKey, iso);
		}
		if (value) {
			sessionStorage.setItem('atr_phone_value_' + fieldKey, value);
		}
		if (iso || value) {
			sessionStorage.setItem('atr_phone_user_interacted', 'true');
		}
	}

	/**
	 * After the phone plugin re-initializes intlTelInput on pagination (which triggers
	 * a fresh async geoIpLookup), restore the user's previously selected country and value.
	 *
	 * Uses a polling interval (every 300ms for up to 4s) instead of a single timeout
	 * because the phone plugin's ipinfo.io lookup is async and may resolve at any time.
	 * Each tick checks whether the geo-lookup has overridden the saved country, and
	 * corrects it. Stops early once all fields match the saved state.
	 */
	restorePhoneStateAfterPagination(phoneInputs) {
		if (!phoneInputs || !phoneInputs.length) return;
		if (sessionStorage.getItem('atr_phone_user_interacted') !== 'true') return;

		let attempts = 0;
		const maxAttempts = 13; // ~4 seconds at 300ms intervals
		const intervalId = setInterval(() => {
			attempts++;
			let allRestored = true;

			phoneInputs.forEach((input) => {
				const fieldKey = this.getPhoneFieldStorageKey(input);
				const savedCountry = sessionStorage.getItem('atr_phone_country_' + fieldKey);
				const savedValue = sessionStorage.getItem('atr_phone_value_' + fieldKey);

				if (!savedCountry && !savedValue) return;

				const iti =
					typeof window !== 'undefined' && window.intlTelInputGlobals && typeof window.intlTelInputGlobals.getInstance === 'function'
						? window.intlTelInputGlobals.getInstance(input)
						: null;

				if (savedCountry && iti && typeof iti.setCountry === 'function') {
					const currentIso = (iti.getSelectedCountryData?.()?.iso2 || '').toLowerCase();
					if (currentIso !== savedCountry) {
						iti.setCountry(savedCountry);
						allRestored = false; // re-check next tick to confirm it stuck
					}
				}

				if (savedValue && (!input.value || input.value.trim() === '')) {
					input.value = savedValue;
					allRestored = false;
				}
			});

			if (allRestored || attempts >= maxAttempts) {
				clearInterval(intervalId);
			}
		}, 300);
	}

	handlePhoneNumberInput() {
		const phoneInputs = this.$root.querySelectorAll('.ginput_container_phone input[type="tel"]');
		const hiddenCountryCode = this.$root.querySelector("input[name='input_96']");
		const hiddenPhone = this.$root.querySelector("input[name='input_98']");
		const primaryPhoneInput = this.$root.querySelector("input[name='input_72_phone']");
		const secondaryPhoneInput = this.$root.querySelector("input[name='input_127_phone']");

		if (!phoneInputs || !phoneInputs.length) {
			return;
		}

		const updateTopLevelPhoneFields = () => {
			if (!hiddenCountryCode || !hiddenPhone) {
				return;
			}

			const activeInput = Array.from(phoneInputs).find((input) => input.value && input.value.trim() !== '');
			const sourceInput = activeInput || phoneInputs[0];
			const sourceContainer = sourceInput.closest('.ginput_container_phone');
			const selectedCountry = sourceContainer?.querySelector('button.iti__selected-country')?.getAttribute('title');
			const selectedCode = sourceContainer?.querySelector('button.iti__selected-country .iti__selected-dial-code')?.textContent;

			hiddenPhone.id = 'phone';

			if (selectedCountry && selectedCountry !== 'Unknown') {
				hiddenCountryCode.value = selectedCountry;
			}

			hiddenPhone.value = `${selectedCode || ''}${sourceInput.value || ''}`;
		};

		const getLastEditedTimestamp = (input) => {
			const parsedTs = Number(input?.dataset?.phoneLastEditedTs || 0);
			return Number.isFinite(parsedTs) ? parsedTs : 0;
		};

		const getIntlTelInputInstance = (input) => {
			if (!input || typeof window === 'undefined' || !window.intlTelInputGlobals || typeof window.intlTelInputGlobals.getInstance !== 'function') {
				return null;
			}

			return window.intlTelInputGlobals.getInstance(input);
		};

		const getPhoneHiddenCountryField = (input) => {
			const phoneContainer = input?.closest('.ginput_container_phone');
			if (!phoneContainer) {
				return null;
			}

			return Array.from(phoneContainer.querySelectorAll('input[type="hidden"]')).find((hiddenInput) => hiddenInput.name.endsWith('_country_code'));
		};

		const getPhoneContainer = (input) => input?.closest('.ginput_container_phone') || null;

		const getSelectedCountryIso = (input) => {
			const itiInstance = getIntlTelInputInstance(input);
			const countryData = itiInstance && typeof itiInstance.getSelectedCountryData === 'function' ? itiInstance.getSelectedCountryData() : null;
			const hiddenCountryField = getPhoneHiddenCountryField(input);
			return (countryData?.iso2 || hiddenCountryField?.value || '').toLowerCase();
		};

		const syncPhoneCountry = (sourceInput, targetInput) => {
			if (!sourceInput || !targetInput) {
				return;
			}

			const sourceCountryIso = getSelectedCountryIso(sourceInput);
			if (!sourceCountryIso) {
				return;
			}

			const targetItiInstance = getIntlTelInputInstance(targetInput);
			if (targetItiInstance && typeof targetItiInstance.setCountry === 'function') {
				targetItiInstance.setCountry(sourceCountryIso);
			} else {
				const sourceContainer = getPhoneContainer(sourceInput);
				const targetContainer = getPhoneContainer(targetInput);
				const sourceSelectedCountryButton = sourceContainer?.querySelector('button.iti__selected-country');
				const targetSelectedCountryButton = targetContainer?.querySelector('button.iti__selected-country');
				const sourceSelectedDialCode = sourceContainer?.querySelector('.iti__selected-dial-code')?.textContent?.trim() || '';
				const targetSelectedDialCodeNode = targetContainer?.querySelector('.iti__selected-dial-code');
				const sourceFlagNode = sourceContainer?.querySelector('.iti__selected-country .iti__flag');
				const targetFlagNode = targetContainer?.querySelector('.iti__selected-country .iti__flag');

				if (sourceSelectedCountryButton && targetSelectedCountryButton) {
					targetSelectedCountryButton.setAttribute(
						'title',
						sourceSelectedCountryButton.getAttribute('title') || targetSelectedCountryButton.getAttribute('title') || '',
					);
				}

				if (targetSelectedDialCodeNode && sourceSelectedDialCode) {
					targetSelectedDialCodeNode.textContent = sourceSelectedDialCode;
				}

				if (sourceFlagNode && targetFlagNode) {
					const sourceFlagClass = Array.from(sourceFlagNode.classList).find((className) => className.startsWith('iti__') && className !== 'iti__flag');
					Array.from(targetFlagNode.classList)
						.filter((className) => className.startsWith('iti__') && className !== 'iti__flag')
						.forEach((className) => targetFlagNode.classList.remove(className));
					if (sourceFlagClass) {
						targetFlagNode.classList.add(sourceFlagClass);
					}
				}
			}

			const targetHiddenCountryField = getPhoneHiddenCountryField(targetInput);
			if (targetHiddenCountryField) {
				targetHiddenCountryField.value = sourceCountryIso;
			}
		};

		const syncPhonePair = (sourceInput, targetInput) => {
			if (!sourceInput || !targetInput) {
				return;
			}

			syncPhoneCountry(sourceInput, targetInput);
			const mirroredValue = sourceInput.value ? sourceInput.value.trim() : '';
			targetInput.value = mirroredValue;

			this.setPhoneFieldInvalidState(sourceInput, this.isPhoneFieldInvalid(sourceInput));
			this.setPhoneFieldInvalidState(targetInput, this.isPhoneFieldInvalid(targetInput));
			this.syncPhoneFieldBeforeSubmit(sourceInput, { dispatchChange: false });
			this.syncPhoneFieldBeforeSubmit(targetInput, { dispatchChange: false });
			updateTopLevelPhoneFields();
		};

		const syncMostRecentPhoneValue = () => {
			if (!primaryPhoneInput || !secondaryPhoneInput) {
				return;
			}

			const primaryTs = getLastEditedTimestamp(primaryPhoneInput);
			const secondaryTs = getLastEditedTimestamp(secondaryPhoneInput);
			const primaryValue = primaryPhoneInput.value ? primaryPhoneInput.value.trim() : '';
			const secondaryValue = secondaryPhoneInput.value ? secondaryPhoneInput.value.trim() : '';

			if (!primaryValue && !secondaryValue) {
				syncPhonePair(primaryPhoneInput, secondaryPhoneInput);
				return;
			}

			if (primaryTs > secondaryTs) {
				syncPhonePair(primaryPhoneInput, secondaryPhoneInput);
				return;
			}

			if (secondaryTs > primaryTs) {
				syncPhonePair(secondaryPhoneInput, primaryPhoneInput);
				return;
			}

			if (secondaryValue) {
				syncPhonePair(secondaryPhoneInput, primaryPhoneInput);
				return;
			}

			syncPhonePair(primaryPhoneInput, secondaryPhoneInput);
		};

		const syncFromEditedInput = (editedInput, triggerType = 'unknown') => {
			if (!editedInput) {
				return;
			}

			editedInput.dataset.phoneLastEditedTs = Date.now().toString();
			this.persistPhoneFieldState(editedInput);

			if (editedInput === primaryPhoneInput && secondaryPhoneInput) {
				syncPhonePair(primaryPhoneInput, secondaryPhoneInput);
				return;
			}

			if (editedInput === secondaryPhoneInput && primaryPhoneInput) {
				syncPhonePair(secondaryPhoneInput, primaryPhoneInput);
				return;
			}

			this.setPhoneFieldInvalidState(editedInput, this.isPhoneFieldInvalid(editedInput));
			this.syncPhoneFieldBeforeSubmit(editedInput, { dispatchChange: false });
			updateTopLevelPhoneFields();
		};

		phoneInputs.forEach((input) => {
			if (input.dataset.phoneHandlerBound === 'true') {
				return;
			}
			input.dataset.phoneHandlerBound = 'true';
			input.addEventListener('input', (event) => {
				// If the first number is a 0, remove it
				if (input.value.startsWith('0')) {
					input.value = input.value.slice(1);
				}

				syncFromEditedInput(input, 'input');
			});

			input.addEventListener('blur', () => {
				syncFromEditedInput(input, 'blur');
			});

			input.addEventListener('change', () => {
				syncFromEditedInput(input, 'change');
			});

			input.addEventListener('countrychange', () => {
				syncFromEditedInput(input, 'countrychange');
			});
		});

		syncMostRecentPhoneValue();

		// Restore phone state after pagination to counteract the phone plugin's
		// async geo-lookup which would otherwise override the user's country selection.
		this.restorePhoneStateAfterPagination(phoneInputs);
	}

	handleNameInputFields() {
		const nameInputs = this.$root.querySelectorAll('input[name="input_40"], input[name="input_41"]');

		if (!nameInputs || !nameInputs.length) {
			return;
		}

		nameInputs.forEach((input) => {
			if (input.dataset.nameHandlerBound === 'true') {
				return;
			}
			input.dataset.nameHandlerBound = 'true';

			input.addEventListener('input', () => {
				this.setNameFieldInvalidState(input, this.isNameFieldInvalid(input));
			});

			input.addEventListener('blur', () => {
				this.setNameFieldInvalidState(input, this.isNameFieldInvalid(input));
			});
		});
	}

	handleEmailInputFields() {
		const emailInputs = this.$root.querySelectorAll('input[type="email"]');

		if (!emailInputs || !emailInputs.length) {
			return;
		}

		emailInputs.forEach((input) => {
			if (input.dataset.emailHandlerBound === 'true') {
				return;
			}
			input.dataset.emailHandlerBound = 'true';

			const updateEmailState = () => {
				this.setEmailFieldInvalidState(input, this.isEmailFieldInvalid(input));
			};

			input.addEventListener('input', updateEmailState);
			input.addEventListener('blur', updateEmailState);
			input.addEventListener('change', updateEmailState);
		});
	}

	/**
	 * Call booking: keep the date/time selection inside the bookable window and
	 * show the guest what their choice means in their own timezone.
	 *
	 * The stored values are UK wall-clock time — that is what the team reads —
	 * but a guest in Los Angeles reads the dropdown in Pacific time and used to
	 * be able to request a slot that had already passed here. So we disable
	 * everything that isn't bookable (past days, weekends, closed dates, slots
	 * outside the calling window) and relabel every slot with both times.
	 *
	 * Re-runs safely: everything is derived from option *values*, never from the
	 * labels we have already written, so repeated gform/post_init events and GF
	 * paging re-renders are harmless.
	 */
	handleCallDateFields() {
		// Hide any datepicker popup left over from page navigation.
		if (typeof jQuery !== 'undefined') {
			jQuery('#ui-datepicker-div').hide();
		}

		const elements = this.callBookingElements();

		if (!elements) {
			return;
		}

		// Listen on the component root, not on the selects themselves. The form
		// pages via Gravity Forms AJAX, which replaces its whole markup on every
		// Next click — by the call step the date/time selects have been swapped
		// out eight times. Listeners bound to the originals died with them, so
		// the initial marking still ran on each render but changing the month
		// never re-marked the days: pick October on the 23rd of September and
		// you got September's pattern, with the 1st-7th disabled. The root is
		// ours, not GF's, so one delegated listener survives every re-render;
		// each change re-resolves the fields to see whether it was one of them.
		if (!this.callBookingBound) {
			this.callBookingBound = true;

			this.$root.addEventListener('change', (event) => {
				const current = this.callBookingElements();

				if (!current) {
					return;
				}

				const controls = [current.year, current.month, current.day, current.time, current.picker];

				if (controls.includes(event.target)) {
					this.refreshCallBooking();
				}
			});
		}

		// Never block first paint on a third-party request: the device's timezone
		// labels the form now, and if the IP lookup says the guest is elsewhere
		// we relabel in place. Runs once per session, whatever it returns.
		if (!this.callBookingGeoRequested) {
			this.callBookingGeoRequested = true;
			lookupGuestGeo(elements.config).then((geo) => {
				if (geo) {
					this.refreshCallBooking();
					this.fillCallBookingLocation();
				}
			});
		}

		this.fillCallBookingLocation();

		this.refreshCallBooking();
	}

	/**
	 * Resolve the call date/time controls.
	 *
	 * Resolution is structural — see findCallBookingFields(). Nothing here keys
	 * off a field id, a form id or an editor-set class, so renumbering the
	 * fields or adding another Start Planning variant doesn't break it.
	 *
	 * @returns {object|null}
	 */
	callBookingElements() {
		if (!this.$root) {
			return null;
		}

		if (!this.callBookingConfig) {
			this.callBookingConfig = readCallBookingConfig(this.$root);
		}

		const fields = findCallBookingFields(this.$root);

		if (!fields.container && !fields.time) {
			return null;
		}

		return { config: this.callBookingConfig, ...fields };
	}

	refreshCallBooking() {
		// Snapping an invalid selection fires change events, which land back
		// here; one pass is enough.
		if (this.callBookingSyncing) {
			return;
		}

		const elements = this.callBookingElements();

		if (!elements) {
			return;
		}

		this.callBookingSyncing = true;

		try {
			const { config } = elements;
			const earliest = earliestBookableDate(config);
			const latest = latestBookableDate(config);

			// Settle on one date first, then mark the dropdowns around it. Doing
			// it the other way — marking year, then month, then day, each step
			// snapping the one before — meant picking a year the current month
			// doesn't exist in threw the guest back to the earliest date, so
			// January was unreachable from December.
			const selectedDate = this.resolveCallBookingDate(elements, earliest, latest);
			const [targetYear, targetMonth, targetDay] = selectedDate.split('-').map(Number);

			this.updateCallBookingYears(elements, earliest, latest);
			this.setCallBookingSelect(elements.year, String(targetYear));

			this.updateCallBookingMonths(elements, earliest, latest, targetYear);
			this.setCallBookingSelect(elements.month, String(targetMonth));

			this.markCallBookingDays(elements, earliest, latest, targetYear, targetMonth);
			this.setCallBookingSelect(elements.day, String(targetDay));

			this.updateCallBookingTimes(elements, selectedDate);
			this.applyCallBookingTimeDefault(elements, selectedDate);
			this.renderCallBookingNote(elements, selectedDate);
			this.syncCallBookingGuestTime(elements, selectedDate);
		} finally {
			this.callBookingSyncing = false;
		}
	}

	/**
	 * Enable/disable one option, keeping the `.disabled` class the styles use.
	 */
	setCallBookingOptionState(option, enabled) {
		option.disabled = !enabled;
		option.classList.toggle('disabled', !enabled);
	}

	/**
	 * The date the dropdowns should settle on, honouring as much of the guest's
	 * choice as the window allows.
	 *
	 * Falls back in order: their exact date, the first bookable day in the month
	 * they picked, the first bookable day in the year they picked, and finally
	 * the earliest bookable date. So changing the year to one where their
	 * current month is out of range moves them to a month that works in that
	 * year rather than undoing the change.
	 *
	 * @returns {string} Y-m-d
	 */
	resolveCallBookingDate(elements, earliest, latest) {
		const { config } = elements;

		const bookable = (y, m, d) => {
			const isoDate = toIsoDate(y, m, d);

			return isoDate >= earliest && isoDate <= latest && isOpenOn(config, y, m, d);
		};

		const firstDayIn = (y, m) => {
			const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();

			for (let d = 1; d <= daysInMonth; d++) {
				if (bookable(y, m, d)) {
					return d;
				}
			}

			return 0;
		};

		// The datepicker variant has one input rather than three selects, so
		// there is no partial state to rescue — keep the guest's date if the
		// window allows it, otherwise start them at the earliest.
		if (!elements.day && elements.picker) {
			const picked = this.selectedCallDate(elements);

			if (picked) {
				const [py, pm, pd] = picked.split('-').map(Number);

				if (bookable(py, pm, pd)) {
					return picked;
				}
			}

			return earliest;
		}

		const year = Number(elements.year?.value) || 0;
		const month = Number(elements.month?.value) || 0;
		const day = Number(elements.day?.value) || 0;

		if (year && month && day && bookable(year, month, day)) {
			return toIsoDate(year, month, day);
		}

		if (year && month) {
			const firstDay = firstDayIn(year, month);

			if (firstDay) {
				return toIsoDate(year, month, firstDay);
			}
		}

		if (year) {
			for (let m = 1; m <= 12; m++) {
				const firstDay = firstDayIn(year, m);

				if (firstDay) {
					return toIsoDate(year, m, firstDay);
				}
			}
		}

		return earliest;
	}

	/**
	 * Only the years the bookable window actually touches. Next year shows up
	 * only once the window reaches into it — i.e. in December.
	 */
	updateCallBookingYears(elements, earliest, latest) {
		if (!elements.year) {
			return;
		}

		const earliestYear = Number(earliest.split('-')[0]);
		const latestYear = Number(latest.split('-')[0]);

		this.ensureCallBookingYearOptions(elements.year, earliestYear, latestYear);

		Array.from(elements.year.options).forEach((option) => {
			if (option.value === '') {
				return;
			}

			const year = Number(option.value);

			this.setCallBookingOptionState(option, year >= earliestYear && year <= latestYear);
		});

		if (elements.year.value !== '' && elements.year.selectedOptions[0]?.disabled) {
			this.setCallBookingSelect(elements.year, String(earliestYear));
		}
	}

	/**
	 * Grow the year dropdown to cover a year it doesn't already offer.
	 *
	 * Day (1-31) and month (1-12) are always rendered in full by Gravity Forms,
	 * so any date within them can be reached just by enabling the right option.
	 * Year is different: GF only renders between gform_date_min_year and
	 * gform_date_max_year, both computed from the REAL server clock the moment
	 * the page was rendered. A page that was live when the window sat entirely
	 * in one year literally has no "next year" option in its markup — no amount
	 * of enabling/disabling reaches a year that was never rendered.
	 *
	 * That gap is real, not just a test artefact: a page cached across a
	 * year-end, or a guest revisiting a tab left open from before New Year,
	 * both leave the dropdown one option short of where the window now sits.
	 * So this adds whatever's missing rather than assuming the render always
	 * has what today's window needs.
	 */
	ensureCallBookingYearOptions(select, earliestYear, latestYear) {
		const known = new Set(Array.from(select.options).map((option) => option.value));

		for (let year = earliestYear; year <= latestYear; year++) {
			const value = String(year);

			if (known.has(value)) {
				continue;
			}

			const option = document.createElement('option');
			option.value = value;
			option.textContent = value;

			// GF lists years highest first; insert before the first existing
			// option that should come after ours, so a newly-added year lands
			// where it would have if the page had rendered it to begin with.
			const before = Array.from(select.options).find((existing) => existing.value !== '' && Number(existing.value) < year);

			select.insertBefore(option, before || null);
		}
	}

	/**
	 * Only the months the window touches — the earliest bookable month and the
	 * next one. Past months of this year go dark, and so does anything further
	 * ahead than we'll take an enquiry call.
	 */
	updateCallBookingMonths(elements, earliest, latest, selectedYear) {
		if (!elements.month) {
			return;
		}

		Array.from(elements.month.options).forEach((option) => {
			if (option.value === '') {
				return;
			}

			const month = Number(option.value);
			const lastDay = new Date(Date.UTC(selectedYear, month, 0)).getUTCDate();

			// A month is open if any day in it falls inside the window.
			const opens = toIsoDate(selectedYear, month, lastDay) >= earliest;
			const closes = toIsoDate(selectedYear, month, 1) <= latest;

			this.setCallBookingOptionState(option, opens && closes);
		});
	}

	/**
	 * Mark each day option bookable or not for the given month. Marking only —
	 * resolveCallBookingDate() decides what ends up selected.
	 */
	markCallBookingDays(elements, earliest, latest, year, month) {
		const { config } = elements;
		const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();

		Array.from(elements.day.options).forEach((option) => {
			if (option.value === '') {
				return;
			}

			const day = Number(option.value);

			if (day > daysInMonth) {
				this.setCallBookingOptionState(option, false);
				return;
			}

			const isoDate = toIsoDate(year, month, day);
			const bookable =
				isoDate >= earliest && isoDate <= latest && isOpenOn(config, year, month, day);

			this.setCallBookingOptionState(option, bookable);
		});
	}

	/**
	 * Relabel every slot with both timezones and disable the ones we can't take.
	 */
	updateCallBookingTimes(elements, isoDate) {
		if (!elements.time) {
			return;
		}

		const { config } = elements;
		const now = Date.now();

		Array.from(elements.time.options).forEach((option) => {
			if (option.value === '') {
				return;
			}

			const minutes = slotToMinutes(option.value);

			if (minutes === null) {
				return;
			}

			const slot = describeSlot(config, isoDate, minutes);
			const inWindow = minutes >= config.firstSlot && minutes <= config.lastSlot;

			this.setCallBookingOptionState(option, inWindow && slot.instant.getTime() > now);
			option.textContent = slotOptionLabel(config, slot);
		});

		if (elements.time.value !== '' && elements.time.selectedOptions[0]?.disabled) {
			const firstBookable = Array.from(elements.time.options).find(
				(option) => option.value !== '' && !option.disabled
			);

			if (firstBookable) {
				this.setCallBookingSelect(elements.time, firstBookable.value);
			}
		}
	}

	/**
	 * Set a select's value and tell everyone else — GF's conditional logic and
	 * our own listeners both watch for change.
	 */
	setCallBookingSelect(select, value) {
		if (!select || select.value === value) {
			return;
		}

		select.value = value;
		select.dispatchEvent(new Event('input', { bubbles: true }));
		select.dispatchEvent(new Event('change', { bubbles: true }));
	}

	/**
	 * Stop a page advancing while a phone number on it is invalid.
	 *
	 * The international phone add-on shows a validation note but never blocks
	 * the Next button, so an unusable number could be carried through to
	 * submission. Its own `gfipm_valids` hidden field can't be used for this —
	 * it records a code only while the input is EMPTY and clears it as soon as
	 * anything is typed — and its error note is removed again on every keyup, so
	 * sniffing the DOM for a visible message misses a guest who types a bad
	 * number and clicks Next without leaving the field. We ask intl-tel-input
	 * itself instead, which is what the add-on validates with.
	 *
	 * @param {Element} button The Next/Submit button that was clicked.
	 * @returns {boolean} True when the click should be blocked.
	 */
	callBookingPhoneInvalid(button) {
		const form = button.closest('form');

		if (!form) {
			return false;
		}

		// Only the page being left matters; other pages are hidden.
		const page = button.closest('.gform_page') || form;
		const inputs = Array.from(page.querySelectorAll('.ginput_container_phone input[type="tel"]')).filter(
			(input) => input.offsetParent !== null
		);

		let blocked = false;

		inputs.forEach((input) => {
			const value = input.value.trim();

			if (value === '') {
				// Empty is Gravity Forms' business, not ours.
				return;
			}

			const iti = this.intlTelInstance(input);

			if (!iti) {
				// No instance to ask — fall back to the add-on's own note, which
				// is better than nothing and never blocks a valid number.
				const message = input.closest('.ginput_container_phone')?.querySelector('.gfipm-error-msg');

				if (message && message.offsetParent !== null && message.textContent.trim() !== '') {
					blocked = true;
				}

				return;
			}

			const valid = typeof iti.isValidNumberPrecise === 'function'
				? iti.isValidNumberPrecise()
				: typeof iti.isValidNumber === 'function' && iti.isValidNumber();

			if (valid) {
				return;
			}

			blocked = true;

			// Make the add-on redraw its own message, so the guest sees why they
			// are being held up rather than a button that silently does nothing.
			input.dispatchEvent(new FocusEvent('blur'));

			if (!this.callBookingPhoneFocused) {
				this.callBookingPhoneFocused = true;
				input.focus();
				window.setTimeout(() => {
					this.callBookingPhoneFocused = false;
				}, 0);
			}
		});

		return blocked;
	}

	/**
	 * The intl-tel-input instance for a field, across both the v17 and v18 APIs.
	 */
	intlTelInstance(input) {
		try {
			if (window.intlTelInput && typeof window.intlTelInput.getInstance === 'function') {
				return window.intlTelInput.getInstance(input);
			}

			if (window.intlTelInputGlobals && typeof window.intlTelInputGlobals.getInstance === 'function') {
				return window.intlTelInputGlobals.getInstance(input);
			}
		} catch (error) {
			// Plugin missing or mid-teardown; treat as "can't tell".
		}

		return null;
	}

	/**
	 * The currently selected call date as Y-m-d, or null while incomplete.
	 */
	selectedCallDate(elements) {
		if (!elements.day && elements.picker) {
			return this.datepickerCallDate(elements.picker);
		}

		const year = Number(elements.year?.value);
		const month = Number(elements.month?.value);
		const day = Number(elements.day?.value);

		if (!year || !month || !day) {
			return null;
		}

		return toIsoDate(year, month, day);
	}

	/**
	 * Read the datepicker variant's date, parsed with jQuery UI's own format so
	 * we never have to guess at the field's dateFormat.
	 *
	 * @returns {string|null} Y-m-d
	 */
	datepickerCallDate(input) {
		if (!input || !input.value) {
			return null;
		}

		try {
			const $ = window.jQuery;
			const instance = $ && $.datepicker ? $(input).data('datepicker') : null;
			const format = instance?.settings?.dateFormat || $.datepicker._defaults.dateFormat;
			const parsed = $.datepicker.parseDate(format, input.value);

			return toIsoDate(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
		} catch (error) {
			return null;
		}
	}

	/**
	 * The line under the fields that spells the booking out in both timezones —
	 * the thing that stops a guest assuming "10:00am" means their morning.
	 */
	/**
	 * Pre-select 10am in the guest's own timezone, per the client's brief.
	 *
	 * PHP can only offer a UK-time placeholder — the page is cached, so it can't
	 * know where the guest is — so the browser corrects it once it does. We only
	 * touch the field while it still holds that placeholder or a value we set
	 * ourselves; a slot the guest chose, or one restored from their last
	 * enquiry, is left alone. Re-runs when the IP lookup lands and moves the
	 * guest to a different timezone than their device claimed.
	 */
	applyCallBookingTimeDefault(elements, isoDate) {
		const { config } = elements;

		if (!elements.time) {
			return;
		}

		const zone = guestTimeZone();

		if (this.callBookingDefaultZone === zone) {
			return;
		}

		const current = elements.time.value;
		const ours = current === '' || current === config.defaultSlot || current === this.callBookingAutoTime;

		this.callBookingDefaultZone = zone;

		if (!ours) {
			return;
		}

		const best = slotNearestLocalTime(config, isoDate, config.defaultLocalMinutes);

		if (best && best.value !== current) {
			this.callBookingAutoTime = best.value;
			this.setCallBookingSelect(elements.time, best.value);
		}
	}

	/**
	 * Keep the hidden "their time" field in step with the time dropdown.
	 *
	 * The dropdown submits UK time — its values are the UK slots, only the
	 * labels are converted — so the guest's own time would otherwise be lost
	 * once they leave the page. Written on every refresh, so it follows the
	 * guest's latest choice and the IP lookup's timezone correction. The field
	 * is found by the class PHP puts on it, never by id; if it hasn't been added
	 * to the form yet this does nothing and the entry just lacks their side.
	 */
	syncCallBookingGuestTime(elements, isoDate) {
		const input = this.$root.querySelector('.pegasus-guest-call-time input');

		if (!input) {
			return;
		}

		const minutes = elements.time ? slotToMinutes(elements.time.value) : null;
		const value = minutes === null ? '' : guestTimeDescription(describeSlot(elements.config, isoDate, minutes));

		if (input.value !== value) {
			input.value = value;
		}
	}

	/**
	 * Suggest the guest's location from the IP lookup.
	 *
	 * This used to be done in PHP during render, which on a page-cached site
	 * meant one visitor's region was baked into the HTML and served to everyone
	 * after them — the reason it looked broken. Doing it here is per-visitor and
	 * costs no extra request: it's the same ipinfo response the phone field and
	 * the timezone labels already use.
	 *
	 * Only ever fills a blank field, so anything the guest types — or anything
	 * restored from their last enquiry — stands.
	 */
	fillCallBookingLocation() {
		if (!this.$root || this.callBookingLocationFilled) {
			return;
		}

		const input = this.$root.querySelector('.pegasus-guest-location input');

		if (!input || input.value.trim() !== '') {
			return;
		}

		const place = guestPlace();

		if (place === '') {
			return;
		}

		this.callBookingLocationFilled = true;
		input.value = place;
		input.dispatchEvent(new Event('input', { bubbles: true }));
		input.dispatchEvent(new Event('change', { bubbles: true }));
	}

	renderCallBookingNote(elements, isoDate) {
		const { config } = elements;

		if (!elements.container && !elements.time) {
			return;
		}

		const host = (elements.container || elements.time.closest('.gfield'))?.closest('.gform_fields');

		if (!host) {
			return;
		}

		let note = host.querySelector('.call-booking-note');

		if (!note) {
			note = document.createElement('div');
			note.className = 'call-booking-note';
			note.setAttribute('aria-live', 'polite');
			host.appendChild(note);
		}

		const timeValue = elements.time ? elements.time.value : '';
		const minutes = slotToMinutes(timeValue);

		if (minutes === null) {
			note.textContent = 'Times are shown in your own timezone — pick one that suits you.';
			return;
		}

		const slot = describeSlot(config, isoDate, minutes);
		const siteZone = timeZoneAbbreviation(config.timezone, slot.instant);
		const guestZone = guestZoneAbbreviation(slot.guestZone, slot.instant);

		// The guest's own day, which is the one they care about, and can be the
		// day after ours for a slot late in our evening.
		const guestDate = this.formatCallBookingDate(slot.dayShift === 0 ? isoDate : slot.localIsoDate);

		// The reminder to fix a wrong auto-filled location applies whichever
		// branch we're in below — a UK guest travelling abroad needs it just as
		// much as an overseas one.
		const locationReminder = 'Kindly update your location if this is not the location you will be in at the time of the call.';

		if (slot.matchesSite) {
			note.textContent =
				`We'll call you on ${guestDate} at ${slot.localLabel}${siteZone ? ` (${siteZone})` : ''}. ` +
				locationReminder;
			return;
		}

		// The city the IP lookup gave us reads better than the timezone's name:
		// a guest in Boston is on New York time, but "Boston" is what they'd
		// recognise as where they are.
		const place = guestPlace() || prettyTimeZoneName(slot.guestZone);

		note.textContent =
			`Times are shown in your own timezone${place ? `, ${place}` : ''}. ` +
			`We'll call you on ${guestDate} at ${slot.localLabel}${guestZone ? ` ${guestZone}` : ''}. ` +
			locationReminder;
	}

	/**
	 * "Monday 24 August" from a Y-m-d string, with no timezone shifting.
	 */
	formatCallBookingDate(isoDate) {
		const [year, month, day] = isoDate.split('-').map(Number);

		return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-GB', {
			weekday: 'long',
			day: 'numeric',
			month: 'long',
			timeZone: 'UTC',
		});
	}

	goToLastPage() {
		if (pegasus.getCookie('lastPage')) {
			window.location.href = pegasus.getCookie('lastPage');
		} else {
			window.location.href = '/';
		}
	}
}

document.addEventListener('alpine:init', () => {
	Alpine.data('getInTouchForm', () => new GetInTouchForm());
});
